import { useState, useCallback, useEffect } from 'react';
import { Pagination, Response, SelectItemProps } from '@ui/src';
import { useAppSelector } from '@store';
import { PspAccount } from '../../../types/psp-account';
import { useLazyGetTransactionsPrimaryAccountsQuery } from '../services';

interface ListResponse {
  data: Response<PspAccount>;
}

let timeout: NodeJS.Timeout;

const useGetPrimaryAccountsAutocompleteList = (
  id?: string,
  currencyId?: string,
  paymentsOrder?: boolean,
) => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [accountsList, setAccountsList] = useState<PspAccount[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const { overdraftEnabled } = useAppSelector(
    (state) => state.transactionsDictionaries,
  );

  const [getList, { isFetching, isLoading }] =
    useLazyGetTransactionsPrimaryAccountsQuery();

  const getTransactionPspAccountByIdAction = async (accountNumber: string) => {
    if (id) {
      const response = (await getList({
        id,
        query: `?all=true&fullNumber=${accountNumber}`,
      })) as ListResponse;

      if (response?.data?.data?.edges?.length) {
        setAutocompleteList(
          response?.data?.data?.edges?.map((item) => ({
            name: item.node.description || '',
            description: item?.node?.realNumberOrIban || '',
            value:
              (paymentsOrder
                ? item.node.account?.id
                : item.node.account?.fullNumber) || '',
            suffix: `${item.node.account?.fullNumber} ${
              // eslint-disable-next-line no-nested-ternary
              overdraftEnabled
                ? item?.node?.account?.overdraftEnabled
                  ? '(Overdraft)'
                  : ''
                : ''
            }`,
          })),
        );
        setAccountsList(response?.data?.data?.edges?.map((item) => item.node));
      }
    }
  };

  const getAutocompleteListAction = useCallback(
    async (currencyValue?: string) => {
      const results: SelectItemProps[] = [];
      const accounts: PspAccount[] = [];

      if (id) {
        const response = (await getList({
          id,
          query: `?take=20&active=true${
            currencyId ? `&currencyId=${currencyValue || currencyId}` : ''
          }`,
        })) as ListResponse;

        if (response?.data?.data) {
          response.data.data.edges?.forEach((item) => {
            results.push({
              name: item.node.description || '',
              description: item?.node?.realNumberOrIban || '',
              value:
                (paymentsOrder
                  ? item.node.account?.id
                  : item.node.account?.fullNumber) || '',
              suffix: `${item.node.account?.fullNumber} ${
                // eslint-disable-next-line no-nested-ternary
                overdraftEnabled
                  ? item?.node?.account?.overdraftEnabled
                    ? '(Overdraft)'
                    : ''
                  : ''
              }`,
            });

            accounts.push(item.node);
          });

          setPagination(response?.data?.data?.pageInfo);
        }
      }
      setAutocompleteList(results);
      setAccountsList(accounts);
    },
    [id],
  );

  const onScroll = async () => {
    setNextPageIsLoading(true);

    const response = (await getList({
      id: id as string,
      query: `?take=20&active=true${
        currencyId ? `&currencyId=${currencyId}` : ''
      }&cursor=${pagination?.endCursor}${searchValue ? `&description=${searchValue}` : ''}`,
    })) as ListResponse;

    if (response?.data?.data) {
      const nextAutocompleteList = ([] as SelectItemProps[]).concat(
        autocompleteList,
      );
      const nextAccountsList = ([] as PspAccount[]).concat(accountsList);

      response?.data?.data?.edges?.forEach((item) => {
        if (
          !nextAutocompleteList?.find(
            (listItem) => listItem.value === item.node.id,
          )
        ) {
          nextAccountsList.push(item.node);
          nextAutocompleteList.push({
            name: item.node.description || '',
            description: item?.node?.realNumberOrIban || '',
            value:
              (paymentsOrder
                ? item.node.account?.id
                : item.node.account?.fullNumber) || '',
            suffix: `${item.node.account?.fullNumber} ${
              // eslint-disable-next-line no-nested-ternary
              overdraftEnabled
                ? item?.node?.account?.overdraftEnabled
                  ? '(Overdraft)'
                  : ''
                : ''
            }`,
          });
        }
      });

      setAutocompleteList(nextAutocompleteList);
      setAccountsList(nextAccountsList);
      setNextPageIsLoading(false);
      setPagination(response?.data?.data?.pageInfo);
    }
  };

  const onSearch = async (search: string) => {
    setSearchValue(search);
    clearTimeout(timeout);

    timeout = setTimeout(async () => {
      const response = (await getList({
        id: id as string,
        query: `?take=20&active=true${
          currencyId ? `&currencyId=${currencyId}` : ''
        }${search ? `&description=${search}` : ''}`,
      })) as ListResponse;

      if (response?.data?.data) {
        const results: SelectItemProps[] = [];
        const accounts: PspAccount[] = [];

        response.data.data.edges?.forEach((item) => {
          results.push({
            name: item.node.description || '',
            description: item?.node?.realNumberOrIban || '',
            value:
              (paymentsOrder
                ? item.node.account?.id
                : item.node.account?.fullNumber) || '',
            suffix: `${item.node.account?.fullNumber} ${
              // eslint-disable-next-line no-nested-ternary
              overdraftEnabled
                ? item?.node?.account?.overdraftEnabled
                  ? '(Overdraft)'
                  : ''
                : ''
            }`,
          });

          accounts.push(item.node);
        });

        setPagination(response?.data?.data?.pageInfo);
        setAutocompleteList(results);
        setAccountsList(accounts);
      }
    }, 700);
  };

  const onClear = () => {
    getAutocompleteListAction();
  };

  useEffect(() => {
    getAutocompleteListAction();
  }, [getAutocompleteListAction]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    onSearch,
    isLoading: isLoading || isFetching,
    accountsList,
    nextPageIsLoading,
    pagination,
    onScroll,
    getTransactionPspAccountByIdAction,
    onClear,
  };
};

export default useGetPrimaryAccountsAutocompleteList;
